
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getOrganizations, createOrganization } from '@/services/organizationService';
import { Checkbox } from '@/components/ui/checkbox';

// Generic email providers we should NOT auto-suggest as org names
const GENERIC_EMAIL_DOMAINS = new Set([
  'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com',
  'aol.com', 'proton.me', 'protonmail.com', 'live.com', 'msn.com',
  'me.com', 'mac.com', 'gmx.com', 'yandex.com', 'zoho.com',
]);

function extractDomain(email: string): string | null {
  const at = email.indexOf('@');
  if (at < 0) return null;
  const domain = email.slice(at + 1).trim().toLowerCase();
  if (!domain || !domain.includes('.')) return null;
  return domain;
}

function suggestOrgNameFromDomain(domain: string): string {
  const root = domain.split('.')[0] || domain;
  return root.charAt(0).toUpperCase() + root.slice(1);
}

const NEW_ORG_VALUE = '__new_org__';

async function getFunctionErrorMessage(error: unknown): Promise<string> {
  if (typeof error === 'object' && error !== null) {
    const err = error as { message?: string; context?: unknown; details?: string };
    // Supabase FunctionsHttpError exposes the underlying Response on `context`
    const ctx = err.context;
    if (ctx && typeof (ctx as Response).clone === 'function') {
      try {
        const body = await (ctx as Response).clone().json();
        if (body && typeof body.error === 'string') return body.error;
        if (body && typeof body.message === 'string') return body.message;
      } catch {
        try {
          const text = await (ctx as Response).clone().text();
          if (text) return text;
        } catch {
          /* ignore */
        }
      }
    }
    if (typeof err.message === 'string') return err.message;
    if (typeof err.details === 'string') return err.details;
  }
  return 'Failed to send invitation';
}

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserInvited: () => void;
  forPlatformAdmins?: boolean;
}

export function InviteUserDialog({ open, onOpenChange, onUserInvited, forPlatformAdmins = false }: InviteUserDialogProps) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'admin' | 'editor' | 'viewer'>('admin');
  const [userType, setUserType] = useState<'platform_admin' | 'org_admin' | 'team_member'>('platform_admin');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('no-organization');
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();
  const { user, isPlatformAdmin } = useAuth();

  // Fetch organizations for organization users page
  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: getOrganizations,
    enabled: isPlatformAdmin && open && !forPlatformAdmins,
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setEmail('');
      setFullName('');
      setRole('admin');
      setUserType(forPlatformAdmins ? 'platform_admin' : 'team_member');
      setSelectedOrganizationId(forPlatformAdmins ? 'no-organization' : (organizations[0]?.id || ''));
      setIsPremium(false);
      setError('');
    }
  }, [open, forPlatformAdmins, organizations]);

  // Update user type based on context
  useEffect(() => {
    if (forPlatformAdmins) {
      setUserType('platform_admin');
      setSelectedOrganizationId('no-organization');
    } else if (selectedOrganizationId && selectedOrganizationId !== 'no-organization') {
      setUserType('team_member');
    }
  }, [selectedOrganizationId, forPlatformAdmins]);

  // Auto-select org based on email domain
  useEffect(() => {
    if (forPlatformAdmins || !email) return;
    const domain = extractDomain(email);
    if (!domain || GENERIC_EMAIL_DOMAINS.has(domain)) return;
    const suggested = suggestOrgNameFromDomain(domain);
    const match = organizations.find(
      (o) => (o.domain || '').toLowerCase() === domain ||
             o.name.toLowerCase() === suggested.toLowerCase()
    );
    if (match) {
      setSelectedOrganizationId(match.id);
    } else {
      setSelectedOrganizationId(NEW_ORG_VALUE);
    }
  }, [email, organizations, forPlatformAdmins]);

  const emailDomain = extractDomain(email);
  const isGenericDomain = emailDomain ? GENERIC_EMAIL_DOMAINS.has(emailDomain) : true;
  const suggestedOrgName = emailDomain && !isGenericDomain ? suggestOrgNameFromDomain(emailDomain) : null;

  const matchingOrg = emailDomain
    ? organizations.find((o) => (o.domain || '').toLowerCase() === emailDomain ||
        o.name.toLowerCase() === (suggestedOrgName?.toLowerCase() || ''))
    : null;

  const showNewOrgOption = !forPlatformAdmins && !!suggestedOrgName && !matchingOrg;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let organizationIdToUse = selectedOrganizationId;

      // If user chose to create a new org from the email domain, create it now
      if (!forPlatformAdmins && selectedOrganizationId === NEW_ORG_VALUE) {
        if (!suggestedOrgName || !emailDomain) {
          throw new Error('Could not determine a new organization name from this email.');
        }
        const newOrg = await createOrganization({
          name: suggestedOrgName,
          domain: emailDomain,
          created_by: user?.id || '',
        });
        organizationIdToUse = newOrg.id;
      }

      const invitationData = {
        email,
        fullName,
        role,
        invitedBy: user?.id,
        userType: forPlatformAdmins ? 'platform_admin' : userType,
        organizationId: forPlatformAdmins ? undefined : organizationIdToUse,
        isPremium: forPlatformAdmins ? false : isPremium
      };

      const { data, error } = await supabase.functions.invoke('send-invitation', {
        body: invitationData
      });

      if (error) {
        throw error;
      }

      if (data?.error) {
        setError(data.error);
        return;
      }

      const invitationType = forPlatformAdmins ? 'platform admin' : 'organization member';
      const premiumText = (!forPlatformAdmins && isPremium) ? ' with full assessment access' : '';
      
      toast({
        title: "Invitation sent successfully",
        description: `${fullName || email} has been invited as ${role} ${invitationType}${premiumText}. They will receive an email with setup instructions.`,
      });

      onUserInvited();
      onOpenChange(false);
    } catch (error: any) {
      setError(await getFunctionErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const getDialogTitle = () => {
    if (forPlatformAdmins) {
      return 'Invite New Platform Admin';
    }
    return 'Invite Organization User';
  };

  const getDialogDescription = () => {
    if (forPlatformAdmins) {
      return 'Invite a new platform administrator. They will receive an email with setup instructions and admin dashboard access.';
    }
    return 'Invite a new user to join an organization. They will receive an email with setup instructions and access to organization features.';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>
            {getDialogDescription()}
          </DialogDescription>
        </DialogHeader>
        
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="John Smith"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={loading}
              />
            </div>

            {!forPlatformAdmins && (
              <div className="space-y-2">
                <Label htmlFor="organization">Organization *</Label>
                <Select value={selectedOrganizationId} onValueChange={setSelectedOrganizationId} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {showNewOrgOption && (
                      <SelectItem value={NEW_ORG_VALUE}>
                        Add "{suggestedOrgName}" (new organization)
                      </SelectItem>
                    )}
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {showNewOrgOption && selectedOrganizationId === NEW_ORG_VALUE && (
                  <p className="text-xs text-muted-foreground">
                    A new organization "{suggestedOrgName}" will be created for domain @{emailDomain}.
                  </p>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Select value={role} onValueChange={(value: 'admin' | 'editor' | 'viewer') => setRole(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {forPlatformAdmins ? (
                    <SelectItem value="admin">Admin - Full access</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="admin">Admin - Full access</SelectItem>
                      <SelectItem value="editor">Editor - Can edit content</SelectItem>
                      <SelectItem value="viewer">Viewer - Read-only access</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {!forPlatformAdmins && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="premium"
                  checked={isPremium}
                  onCheckedChange={(checked) => setIsPremium(checked === true)}
                />
                <Label 
                  htmlFor="premium" 
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Grant full assessment access
                </Label>
              </div>
            )}

            {!forPlatformAdmins && isPremium && (
              <Alert>
                <AlertDescription>
                  This user will have access to full assessment features including advanced analytics, custom branding, and priority support.
                </AlertDescription>
              </Alert>
            )}
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Sending Invitation...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
